import { useTranslation } from "react-i18next"

import type { ImageSettings } from "@/stores/studio-ai-store"

import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Slider } from "@/components/ui/slider"
import { cn } from "@/lib/utils"

type ImageAdvancedStepProps = {
  settings: ImageSettings
  /**
   * False when the chosen model can't produce an alpha channel — the control is
   * hidden and the wizard sends `background: "opaque"` regardless of what the
   * stored setting says.
   */
  supportsTransparent?: boolean
  onSettingsChange: (settings: Partial<ImageSettings>) => void
}

export function ImageAdvancedStep({
  settings,
  supportsTransparent = true,
  onSettingsChange,
}: ImageAdvancedStepProps) {
  const { t } = useTranslation(undefined, { keyPrefix: "studioAi.image.advanced" })

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <h2 className="text-lg font-semibold">{t("title")}</h2>
        <p className="text-muted-foreground text-sm">{t("description")}</p>
      </div>

      {/* Output format */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">{t("outputFormat")}</Label>
        <Select
          value={settings.outputFormat}
          onValueChange={(value) => onSettingsChange({ outputFormat: value as "png" | "jpeg" })}
        >
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="png">PNG</SelectItem>
            <SelectItem value="jpeg">JPEG</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Number of images — temporarily hidden while we migrate to streaming.
          The Azure streaming endpoint yields a single image per call; multi-image
          fan-out is a follow-up. The backend rejects n > 1 explicitly to avoid
          silently dropping frames, so the only safe UI state is fixed at 1. */}

      {/* Background — hidden for models without transparency support */}
      {supportsTransparent && (
        <div className="space-y-2">
          <Label className="text-sm font-medium">{t("background")}</Label>
          <p className="text-muted-foreground text-xs">{t("backgroundDesc")}</p>
          <div className="grid grid-cols-2 gap-3">
            {(["opaque", "transparent"] as const).map((bg) => (
              <button
                key={bg}
                type="button"
                className={cn(
                  "rounded-lg border-2 p-3 text-left transition-colors",
                  "hover:border-primary/50",
                  "focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
                  settings.background === bg ? "border-primary bg-primary/5" : "border-border"
                )}
                onClick={() => onSettingsChange({ background: bg })}
              >
                <p className="text-sm font-medium">{t(bg)}</p>
                <p className="text-muted-foreground text-xs">{t(`${bg}Desc`)}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Compression */}
      {settings.outputFormat !== "png" && (
        <div className="space-y-2">
          <Label className="text-sm font-medium">
            {t("compression")} — {settings.compression}%
          </Label>
          <p className="text-muted-foreground text-xs">{t("compressionDesc")}</p>
          <Slider
            max={100}
            min={10}
            step={5}
            value={[settings.compression]}
            onValueChange={([value]) => onSettingsChange({ compression: value })}
          />
        </div>
      )}
    </div>
  )
}
